/**
 * Frontend entry: optional Sentry, production version-check, and React root render.
 */
import { useEffect } from "react";
import App from "./App.jsx";
import ErrorBoundary from "./components/common/ErrorBoundary";
import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import "./index.css";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import { SENTRY_CONFIG, FEATURE_FLAGS } from "./config";
import VersionCheckService from "./services/versionCheck";
import { VERSION_CHECK_CONFIG } from "./config";

// Conditionally enable debug and Sentry based on the environment
const enableSentry = FEATURE_FLAGS.ENABLE_SENTRY;

if (enableSentry) {
  Sentry.init({
    dsn: SENTRY_CONFIG.DSN,
    sendDefaultPii: SENTRY_CONFIG.sendDefaultPii,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        // In development: unmask for debugging
        // In production: mask text and block media to protect sensitive data
        maskAllText: !SENTRY_CONFIG.debug,
        blockAllMedia: !SENTRY_CONFIG.debug,
        // Mask specific sensitive fields even in development
        maskAllInputs: true,  // Always mask password fields and sensitive inputs
      }),
    ],
    tracesSampleRate: SENTRY_CONFIG.debug ? 1.0 : 0.1,
    debug: SENTRY_CONFIG.debug,
    release: SENTRY_CONFIG.release,
    replaysSessionSampleRate: SENTRY_CONFIG.replaysSessionSampleRate,
    replaysOnErrorSampleRate: SENTRY_CONFIG.replaysOnErrorSampleRate,
  });
}

// Construct the version-check service in production. AuthenticatedApp owns the
// shared interval and calls checkVersion() on the same tick as announcements.
let versionCheckService = null;
if (FEATURE_FLAGS.ENABLE_VERSION_CHECK) {
  versionCheckService = new VersionCheckService({
    checkInterval: VERSION_CHECK_CONFIG.CHECK_INTERVAL,
    versionUrl: VERSION_CHECK_CONFIG.VERSION_URL,
  });
  window.__versionCheckService = versionCheckService;
}

const appContainer = document.getElementById("app");
const root = createRoot(appContainer);

root.render(
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);
